import { useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

export function useVoiceRecorder(onText) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const rec = useRef(null);
  const chunks = useRef([]);

  const stop = useCallback(() => {
    if (rec.current?.stop) rec.current.stop();
    setRecording(false);
  }, []);

  const startBrowser = useCallback(() => {
    const r = new SR();
    r.lang = "ar-SA"; r.continuous = false; r.interimResults = false;
    r.onresult = (e) => { const t = Array.from(e.results).map((x) => x[0].transcript).join(" ").trim(); t ? onText(t) : toast.warning("لم يتم التعرف على كلام واضح"); };
    r.onerror = (e) => { if (e.error !== "aborted") toast.error(e.error === "not-allowed" ? "اسمح للمتصفح باستخدام الميكروفون" : "تعذر التعرف الصوتي: " + e.error); };
    r.onend = () => setRecording(false);
    r.start();
    rec.current = r;
    setRecording(true);
  }, [onText]);

  const startDeepgram = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4" });
    chunks.current = [];
    mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    mr.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks.current, { type: mr.mimeType });
      if (blob.size < 1000) return;
      setTranscribing(true);
      try {
        const fd = new FormData(); fd.append("file", blob, "voice.webm");
        const { data } = await api.post("/voice/transcribe", fd);
        data.text ? onText(data.text) : toast.warning("لم يتم التعرف على كلام واضح");
      } catch (e) { toast.error(errMsg(e)); }
      finally { setTranscribing(false); }
    };
    mr.start();
    rec.current = mr;
    setRecording(true);
  }, [onText]);

  const start = useCallback(async () => {
    try {
      let useDeepgram = false;
      try { useDeepgram = (await api.get("/voice/status")).data.deepgram; } catch { /* ignore */ }
      if (useDeepgram) await startDeepgram();
      else if (SR) startBrowser();
      else await startDeepgram();
    } catch { toast.error("لا يمكن الوصول إلى الميكروفون"); }
  }, [startBrowser, startDeepgram]);

  return { recording, transcribing, start, stop, toggle: () => (recording ? stop() : start()) };
}

export const speak = (text) => {
  if (!("speechSynthesis" in window) || !text) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text.replace(/[*#_`>-]/g, ""));
  u.lang = "ar-SA";
  const v = window.speechSynthesis.getVoices().find((x) => x.lang.startsWith("ar"));
  if (v) u.voice = v;
  window.speechSynthesis.speak(u);
};
